import {
  IGetAllLocalizationLanguagesBodyRes,
  IGetAllLocalizedLocalMapKeysAndTranslationsByLanguageRes,
  IGetProductCategoryKeysAndTranslationsByLanguageRes,
  IGetProductColorKeysAndTranslationsByLanguageRes,
} from "@evennt-tech/api-v2-external";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";
import { LocalizationLanguageConfigurationStep } from "view/apps/Admin/pages/LanguageSettings/utils/constants";
import {
  CommonNotificationType,
  INotificationBaseMessage,
  IPayloadUpdateLocalizationLanguage,
} from "@evennt-tech/api-v2-core";
import { stripEnvKeyPrefix } from "tools/localization/envKeyPrefix";

export type State = {
  activeLangCode: string | null;
  activeStep: LocalizationLanguageConfigurationStep;
  languages: IGetAllLocalizationLanguagesBodyRes[];
  localMapValueByLanguage: Map<
    string,
    IGetAllLocalizedLocalMapKeysAndTranslationsByLanguageRes[]
  >;
  productCategoriesByLanguage: Map<
    string,
    Record<string, IGetProductCategoryKeysAndTranslationsByLanguageRes>
  >;
  productColorsByLanguage: Map<
    string,
    Record<string, IGetProductColorKeysAndTranslationsByLanguageRes>
  >;
  isLanguagesLoading: boolean;
  isLocalMapsLoading: boolean;
  isProductCategoriesLoading: boolean;
  isProductColorsLoading: boolean;
};

export type Action = {
  setActiveStep: (newActiveStep: LocalizationLanguageConfigurationStep) => void;
  setActiveLangCode: (newActiveLangCode: string | null) => void;
  fetchLanguages: (force?: boolean) => Promise<void>;
  fetchLocalMaps: (langCode: string, force?: boolean) => Promise<void>;
  fetchProductCategories: (langCode: string, force?: boolean) => Promise<void>;
  fetchProductColors: (langCode: string, force?: boolean) => Promise<void>;
  autoCatchLanguages: () => () => void;
  reset: () => void;
};

const DEFAULT_STATE: State = {
  activeStep: LocalizationLanguageConfigurationStep.Language,
  activeLangCode: null,
  languages: [],
  localMapValueByLanguage: new Map(),
  productCategoriesByLanguage: new Map(),
  productColorsByLanguage: new Map(),
  isLanguagesLoading: false,
  isLocalMapsLoading: false,
  isProductCategoriesLoading: false,
  isProductColorsLoading: false,
};

type Store = State & Action;

export const localizationLanguageAdminStore = create(
  devtools<Store>(
    (set, get) => ({
      ...DEFAULT_STATE,
      setActiveStep: (newActiveStep) => set({ activeStep: newActiveStep }),
      setActiveLangCode: (newActiveLangCode) => {
        set({ activeLangCode: newActiveLangCode });
      },
      fetchLanguages: async (force = false) => {
        const { isLanguagesLoading, languages } = get();

        if (!force && (isLanguagesLoading || languages.length)) {
          return;
        }

        set({ isLanguagesLoading: true });
        try {
          const languages = await apiV2.localization.getAllLanguages();
          set({ languages, isLanguagesLoading: false });
        } catch (e) {
          console.error("Error occurred fetching languages: ", e);
          set({ isLanguagesLoading: false });
        }
      },
      fetchLocalMaps: async (langCode, force = false) => {
        const { isLocalMapsLoading, localMapValueByLanguage } = get();
        if (
          !force &&
          (isLocalMapsLoading || localMapValueByLanguage.get(langCode))
        ) {
          return;
        }

        set({ isLocalMapsLoading: true });

        try {
          const localizedLocalMaps = (
            await apiV2.localization.getAllLocalizedLocalMapKeysByLanguage({
              langCode,
            })
          ).map(stripEnvKeyPrefix);
          const newLocalMapsByLanguage = new Map(localMapValueByLanguage);

          newLocalMapsByLanguage.set(langCode, localizedLocalMaps);
          set({
            isLocalMapsLoading: false,
            localMapValueByLanguage: newLocalMapsByLanguage,
          });
        } catch (e) {
          console.error("Error occurred fetching localized local maps: ", e);
          set({ isLocalMapsLoading: false });
        }
      },
      fetchProductCategories: async (langCode, force = false) => {
        const {
          isProductCategoriesLoading,
          productCategoriesByLanguage,
        } = get();
        if (
          !force &&
          (isProductCategoriesLoading ||
            productCategoriesByLanguage.get(langCode))
        ) {
          return;
        }

        set({ isProductCategoriesLoading: true });

        try {
          const productCategories = await apiV2.productCategory.getAllKeysAndTranslations(
            { langCode }
          );
          const newProductCategoriesByLanguage = new Map(
            productCategoriesByLanguage
          );
          const productCategoriesMap = productCategories.reduce<
            Record<string, IGetProductCategoryKeysAndTranslationsByLanguageRes>
          >((acc, curr) => {
            const item = stripEnvKeyPrefix(curr);
            acc[item.translationKey] = item;
            return acc;
          }, {});

          newProductCategoriesByLanguage.set(langCode, productCategoriesMap);
          set({
            isProductCategoriesLoading: false,
            productCategoriesByLanguage: newProductCategoriesByLanguage,
          });
        } catch (e) {
          console.error(
            "Error occurred fetching product category translations: ",
            e
          );
          set({ isProductCategoriesLoading: false });
        }
      },
      fetchProductColors: async (langCode, force = false) => {
        const { isProductColorsLoading, productColorsByLanguage } = get();
        if (
          !force &&
          (isProductColorsLoading || productColorsByLanguage.get(langCode))
        ) {
          return;
        }

        set({ isProductColorsLoading: true });

        try {
          const productColors = await apiV2.customProductOption.definition.admin.getKeysAndTranslations(
            { langCode }
          );
          const newProductColorsByLanguage = new Map(productColorsByLanguage);

          const productColorsMap = productColors.reduce<
            Record<string, IGetProductColorKeysAndTranslationsByLanguageRes>
          >((acc, curr) => {
            const item = stripEnvKeyPrefix(curr);
            acc[item.translationKey] = item;
            return acc;
          }, {});
          newProductColorsByLanguage.set(langCode, productColorsMap);
          set({
            isProductColorsLoading: false,
            productColorsByLanguage: newProductColorsByLanguage,
          });
        } catch (e) {
          console.error(
            "Error occurred fetching product color translations: ",
            e
          );
          set({ isProductColorsLoading: false });
        }
      },
      autoCatchLanguages: () => {
        apiV2.notification.subscribe<
          INotificationBaseMessage<IPayloadUpdateLocalizationLanguage>
        >({
          key: CommonNotificationType.UPDATE_LOCALIZATION_LANGUAGE,
          callback: async ({ payload }) => {
            if (!payload) return;

            const {
              languages,
              localMapValueByLanguage,
              productColorsByLanguage,
              productCategoriesByLanguage,
              fetchLanguages,
              fetchLocalMaps,
              fetchProductCategories,
              fetchProductColors,
            } = get();

            const promises: Promise<void>[] = [];
            if (languages.length) {
              promises.push(fetchLanguages(true));
            }

            if (localMapValueByLanguage.get(payload.langCode)) {
              promises.push(fetchLocalMaps(payload.langCode, true));
            }
            if (productCategoriesByLanguage) {
              promises.push(fetchProductCategories(payload.langCode, true));
            }
            if (productColorsByLanguage.get(payload.langCode)) {
              promises.push(fetchProductColors(payload.langCode, true));
            }

            if (promises.length) {
              await Promise.all(promises);
            }
          },
        });

        return () =>
          apiV2.notification.unsubscribe({
            key: CommonNotificationType.UPDATE_LOCALIZATION_LANGUAGE,
          });
      },
      /*
       * Rewinds the wizard, and deliberately keeps the fetched data.
       *
       * The page calls this on unmount and `fetchLanguages()` on mount, and
       * every fetch here short-circuits when its slice is already warm unless
       * `force` is passed. Clearing the maps as well would make those guards
       * dead weight — leaving and re-entering Language Settings would refetch
       * everything — so only the wizard position and the in-flight flags go
       * back to defaults.
       */
      reset: () => {
        set({
          activeStep: DEFAULT_STATE.activeStep,
          activeLangCode: DEFAULT_STATE.activeLangCode,
          isLanguagesLoading: false,
          isLocalMapsLoading: false,
          isProductCategoriesLoading: false,
          isProductColorsLoading: false,
        });
      },
    }),
    {
      name: "localization-language",
    }
  )
);

export const useLocalizationLanguageStore = getOptimizedSelector(
  localizationLanguageAdminStore
);
